import { CompanyIdentifierSystem } from "@/api-schema-enums";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Fingerprint } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CompanyIdentifierFormValues, isDuplicateCompanyIdentifier } from "../company-identifiers";

interface CompanyFormWithIdentifiers {
  companyIdentifiers: CompanyIdentifierFormValues;
}

interface CompanyIdentifierFormCardProps {
  duplicateKeys?: ReadonlySet<string>;
  isCheckingDuplicates?: boolean;
  isSapLocked?: boolean;
}

export function CompanyIdentifierFormCard({
  duplicateKeys = new Set(),
  isCheckingDuplicates = false,
  isSapLocked = false,
}: CompanyIdentifierFormCardProps) {
  const { t } = useTranslation(["masterdata", "common"]);
  const { control } = useFormContext<CompanyFormWithIdentifiers>();

  return (
    <Card className="shadow-none">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2">
          <Fingerprint className="h-4 w-4" />
          {t("masterdata:CompanyIdentifiers")}
        </CardTitle>
        <CardDescription>{t("masterdata:CompanyIdentifiersDescription")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={control}
            name="companyIdentifiers.v1"
            render={({ field }) => {
              const hasDuplicate = isDuplicateCompanyIdentifier(duplicateKeys, CompanyIdentifierSystem.V1, field.value);

              return (
                <FormItem>
                  <FormLabel>{t("masterdata:V1ID")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      placeholder={t("masterdata:V1IdExample")}
                      error={hasDuplicate}
                      readOnly
                      disabled
                    />
                  </FormControl>
                  <FormMessage />
                  {hasDuplicate ? (
                    <p className="text-error text-sm">{t("masterdata:CompanyIdentifierAlreadyExists")}</p>
                  ) : null}
                </FormItem>
              );
            }}
          />

          <FormField
            control={control}
            name="companyIdentifiers.sap"
            render={({ field }) => {
              const hasDuplicate = isDuplicateCompanyIdentifier(
                duplicateKeys,
                CompanyIdentifierSystem.Sap,
                field.value,
              );

              return (
                <FormItem>
                  <FormLabel>{t("masterdata:CompanySapPrefix")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      inputMode="numeric"
                      maxLength={2}
                      placeholder={t("masterdata:CompanySapPrefixPlaceholder")}
                      error={hasDuplicate}
                      onChange={(event) => field.onChange(event.target.value.replace(/\D/g, ""))}
                      readOnly={isSapLocked}
                      disabled={isSapLocked}
                    />
                  </FormControl>
                  {!isSapLocked ? (
                    <p className="text-muted-foreground mt-1 text-sm">{t("masterdata:CompanySapPrefixHint")}</p>
                  ) : null}
                  <FormMessage />
                  {hasDuplicate ? (
                    <p className="text-error text-sm">{t("masterdata:CompanyIdentifierAlreadyExists")}</p>
                  ) : null}
                </FormItem>
              );
            }}
          />
        </div>

        {isCheckingDuplicates ? (
          <p className="text-muted-foreground text-sm">{t("masterdata:CheckingDuplicateCompanyIdentifiers")}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
